import clsx from "clsx";
import { motion } from "framer-motion";

import { useMobileApp } from "@chef/feature-mobile-app";
import { useEnvironment } from "@chef/hooks";

import { DefaultVariant } from "./DefaultVariant";
import { MyMenuVariant } from "./MyMenuVariant";
import { StepsVariant } from "./StepsVariant";
import { SimpleVariant } from "./SimpleVariant";
import { B2BVariant } from "./B2BVariant";
import { SignupVariant } from "./SignupVariant";
import { NavigationProgress } from "./NavigationProgress";
import { useSticky } from "../Sticky";
import { MyDeliveryVariant } from "./MyDeliveryVariant";

import type { Step } from "./types";

type DefaultVariantProps = {
  variant?: "default";
};

type SimpleVariantProps = {
  variant: "simple";
} & (
  | ({ exitLabel: string } & (
      | {
          onClose: () => void;
        }
      | {
          onBack: () => void;
        }
      | {
          closeHref: string;
        }
      | {
          backHref: string;
        }
    ))
  | { exitLabel?: never }
);

type MyMenuVariantProps = {
  variant: "my-menu";
};

type MyDeliveryVariantProps = {
  variant: "my-delivery";
};

type B2BVariantProps = {
  variant: "b2b";
};

type SignupVariantProps = {
  variant: "signup";
  current: number;
  steps: Step[];
};

type BaseStepsVariantProps = {
  variant: "steps";
} & (
  | {
      onClose: () => void;
      exitLabel: string;
    }
  | {
      onBack: () => void;
      exitLabel: string;
    }
  | {
      backHref: string;
      exitLabel: string;
    }
  | {
      closeHref: string;
      exitLabel: string;
    }
  | {
      exitLabel?: never;
    }
) &
  (
    | {
        onCartClick: () => void;
        cartCount: number;
      }
    | { onCartClick?: never; cartCount?: never }
  );

type StepsVariantWithSteps = BaseStepsVariantProps & {
  current: number;
  steps: Step[];
  title?: never;
};

type StepsVariantWithStep = BaseStepsVariantProps & {
  current?: never;
  title: string;
  steps?: never;
};

type StepsVariantProps = StepsVariantWithSteps | StepsVariantWithStep;

type HeaderProps = {
  printHidden?: boolean;
  title?: string;
} & (
  | DefaultVariantProps
  | StepsVariantProps
  | SimpleVariantProps
  | B2BVariantProps
  | SignupVariantProps
  | MyMenuVariantProps
  | MyDeliveryVariantProps
);
const isStepsVariant = (props: HeaderProps): props is StepsVariantProps => {
  return props.variant === "steps";
};

const isDefaultVariant = (props: HeaderProps): props is DefaultVariantProps => {
  return props.variant === "default" || !props.variant;
};

const isSimpleVariant = (props: HeaderProps): props is SimpleVariantProps => {
  return props.variant === "simple";
};

const isB2BVariant = (props: HeaderProps): props is B2BVariantProps => {
  return props.variant === "b2b";
};

const isSignupVariant = (props: HeaderProps): props is SignupVariantProps => {
  return props.variant === "signup";
};

const isMyMenuVariant = (props: HeaderProps): props is MyMenuVariantProps => {
  return props.variant === "my-menu";
};

const isMyDeliveryVariant = (
  props: HeaderProps,
): props is MyMenuVariantProps => {
  return props.variant === "my-delivery";
};

export const Header = (props: HeaderProps) => {
  const { isOldApp } = useMobileApp();

  const { isFloating } = useSticky();

  const isDefault = isDefaultVariant(props);
  const isSteps = isStepsVariant(props);
  const isSimple = isSimpleVariant(props);
  const isB2B = isB2BVariant(props);
  const isSignup = isSignupVariant(props);
  const isMyMenu = isMyMenuVariant(props);
  const isMyDelivery = isMyDeliveryVariant(props);

  const { isWebView } = useEnvironment();

  return (
    <>
      <NavigationProgress />
      <motion.header
        className={clsx(
          !isFloating && "page-padding",
          !isMyMenu && "bg-background",
          props.printHidden && "print:hidden",
          isOldApp && !isWebView && "pt-12",
        )}
      >
        <div className={clsx(!isSignup && "page-max-width")}>
          {isDefault && <DefaultVariant />}
          {isSteps && <StepsVariant {...props} />}
          {isSimple && <SimpleVariant {...props} />}
          {isB2B && <B2BVariant />}
          {isSignup && <SignupVariant {...props} />}
          {isMyMenu && <MyMenuVariant />}
          {isMyDelivery && <MyDeliveryVariant />}
        </div>
      </motion.header>
    </>
  );
};
